// Home do Atrio Flow — "Cockpit de Fechamento" (premium, dados vivos, NAO lista de links).
// Reusa endpoints/componentes do Dashboard. Honesto: so mostra o que o backend sabe.
import { useEffect, useState } from 'react'
import {
  obterClienteApi, faturamento12, dre,
  listarPendenciasReinf, listarTxSemLcto, certsAlerta, listarApuracoesDAS,
  listarApuracoesPisCofins, listarApuracoesIrpjCsll,
} from '../api'
import { KPI, PageHeader, Loading, Empty, Alert } from '../components/common'
import { fmtMoeda, fmtMoedaCompacta, competenciaLabel, mesAtual } from '../lib/format'

export default function Home({ clienteId, onNavigate }) {
  const [d, setD] = useState(null)
  const [cliente, setCliente] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!clienteId) { setLoading(false); return }
    setLoading(true)
    const comp = mesAtual(); const [ano, mes] = comp.split('-')
    Promise.allSettled([
      obterClienteApi(clienteId),
      faturamento12(clienteId, { ano, mes }),
      dre(clienteId, { ano, mes, tipo: 'mensal' }),
      listarPendenciasReinf(clienteId, { per_apur: comp }),
      listarTxSemLcto(clienteId),
      certsAlerta(),
      listarApuracoesDAS(clienteId, { ano }),
      listarApuracoesPisCofins(clienteId, { ano }),
      listarApuracoesIrpjCsll(clienteId, { ano }),
    ]).then(([cl, f, dr, pr, pf, c, das, pc, ir]) => {
      setCliente(cl.status === 'fulfilled' ? cl.value : null)
      setD({
        fat: f.status === 'fulfilled' ? f.value?.relatorio : null,
        dre: dr.status === 'fulfilled' ? dr.value?.relatorio : null,
        pendReinf: pr.status === 'fulfilled' ? (pr.value?.count || 0) : 0,
        pendFin: pf.status === 'fulfilled' ? (pf.value?.count || 0) : 0,
        certs: c.status === 'fulfilled' ? (c.value?.items || []) : [],
        das: das.status === 'fulfilled' ? (das.value?.items || []) : [],
        pc:  pc.status === 'fulfilled' ? (pc.value?.items || []) : [],
        ir:  ir.status === 'fulfilled' ? (ir.value?.items || []) : [],
      })
      setLoading(false)
    })
  }, [clienteId])

  if (!clienteId) return <Empty title="Selecione um cliente" sub="Use o seletor no topo pra abrir o cockpit." />
  if (loading || !d) return <Loading />

  const comp = mesAtual()
  const k = d.fat?.kpis || {}
  const totais = d.dre?.totais || {}
  const certsCrit = d.certs.filter(c => ['critico', 'vencido'].includes(c.severidade))
  // Regime manda no cockpit: DAS e guia do Simples; presumido/real vive de
  // PIS/COFINS mensal + IRPJ/CSLL trimestral (erro de regra apontado pelo Caio
  // com a BFA: holding em Presumido sendo cobrada por DAS).
  const regime = (cliente?.regime_efetivo || cliente?.regime_override || cliente?.regime || '').toLowerCase()
  const isSimples = regime.includes('simples') || regime.includes('mei')
  const dasMes = d.das.find(a => (a.competencia || '').startsWith(comp))
  const dasRasc = d.das.filter(a => (a.status || '').toLowerCase() === 'rascunho').length
  const dasAprov = d.das.filter(a => (a.status || '').toLowerCase() === 'aprovada').length
  const pcMes = d.pc.find(a => (a.competencia || '').startsWith(comp))
  const mesNum = Number(comp.slice(5, 7))
  const triAnterior = Math.floor((mesNum - 1) / 3) // 1..4 do tri fechado (0 = ano anterior)
  const irTriAnterior = triAnterior > 0 && d.ir.find(a => Number(a.trimestre) === triAnterior)
  const mesPosTri = [1, 4, 7, 10].includes(mesNum)

  // Guia do mes conforme o regime (KPI e modulo Fiscal)
  const guiaLabel = isSimples ? 'DAS do mês' : 'PIS/COFINS (mês)'
  const guiaMes = isSimples ? dasMes : pcMes
  const guiaGo = isSimples ? 'apuracao-das' : 'apuracao-pis-cofins'
  const guiaNome = isSimples ? 'DAS' : 'PIS/COFINS'

  // FAIXA 3 — O que fazer agora (derivado de dado real)
  const acoes = []
  if (certsCrit.length) acoes.push({ urg: true, txt: `${certsCrit.length} certificado(s) ${certsCrit[0].severidade === 'vencido' ? 'vencido(s)' : 'vencendo'} — bloqueia transmissão`, btn: 'Renovar', go: 'certificados' })
  if (!guiaMes) acoes.push({ urg: true, txt: `Apurar ${guiaNome} de ${competenciaLabel(comp)}`, btn: 'Apurar', go: guiaGo })
  if (!isSimples && mesPosTri && !irTriAnterior) acoes.push({ urg: true, txt: `Apurar IRPJ/CSLL do ${triAnterior}º trimestre`, btn: 'Apurar', go: 'apuracao-irpj-csll' })
  if (d.pendFin > 0) acoes.push({ txt: `${d.pendFin} transação(ões) do Finance sem lançamento contábil`, btn: 'Contabilizar', go: 'pendencias' })
  if (d.pendReinf > 0) acoes.push({ txt: `${d.pendReinf} pendência(s) EFD-Reinf (nota sem match)`, btn: 'Resolver', go: 'pendencias' })

  // FAIXA 4 — Saúde dos módulos
  const modulos = [
    { icon: '📚', nome: 'Contábil', tom: d.pendFin > 0 ? 'warn' : 'ok',
      m1: totais.lucro_liquido != null ? `Resultado ${fmtMoeda(totais.lucro_liquido)}` : 'Sem DRE no mês',
      m2: d.pendFin > 0 ? `${d.pendFin} a contabilizar` : 'Lançamentos em dia',
      btn: d.pendFin > 0 ? 'Contabilizar' : 'Lançamentos', go: d.pendFin > 0 ? 'pendencias' : 'lancamentos' },
    { icon: '🧾', nome: 'Fiscal', tom: (!guiaMes || d.pendReinf > 0) ? 'warn' : 'ok',
      m1: isSimples
        ? `DAS: ${dasAprov} aprovada(s) · ${dasRasc} rascunho(s)`
        : `PIS/COFINS: ${d.pc.length} apuração(ões) · IRPJ/CSLL: ${d.ir.length} tri`,
      m2: !guiaMes ? `${guiaNome} ${competenciaLabel(comp)} pendente` : `${guiaNome} do mês apurado`,
      btn: `Apurar ${guiaNome}`, go: guiaGo },
    { icon: '👥', nome: 'Folha', tom: 'neutral',
      m1: 'Importada do Domínio', m2: 'própria no futuro',
      btn: 'Importar Domínio', go: 'import-dominio' },
    { icon: '🏛️', nome: 'Imobilizado', tom: 'neutral',
      m1: 'Bens e depreciação', m2: 'CIAP / crédito',
      btn: 'Abrir Imobilizado', go: 'imobilizado' },
  ]
  const dot = { ok: '#22c55e', warn: '#eab308', neutral: '#64748b' }

  return (
    <>
      <PageHeader
        title={cliente?.razao_social || 'Átrio Flow'}
        sub={`${(cliente?.regime || '—')} · competência ${competenciaLabel(comp)} · cockpit de fechamento`}
      />

      {certsCrit.length > 0 && (
        <Alert tipo="danger">⚠ {certsCrit.length} certificado(s) digital(is) {certsCrit[0].severidade === 'vencido' ? 'vencido(s)' : 'com vencimento crítico'} — Reinf/SPED ficam bloqueados.</Alert>
      )}

      <div style={{ margin: '-4px 0 16px' }}>
        <button onClick={() => onNavigate('resumo-competencia')} style={{ ...btnGo, background: 'rgba(196,149,106,0.18)', padding: '8px 14px' }}>▦ Ver esteira da competência →</button>
      </div>

      {/* FAIXA 2 — KPIs vivos */}
      <div className="kpi-grid">
        <KPI label="Faturamento 12m" value={fmtMoeda(k.total_periodo)} sub={`média ${fmtMoedaCompacta(k.media_mensal)}/mês`} />
        <KPI label="Resultado (mês)" value={fmtMoeda(totais.lucro_liquido)} variant={(totais.lucro_liquido ?? 0) >= 0 ? 'success' : 'danger'} sub="DRE competência" />
        <KPI label={guiaLabel} value={guiaMes ? (guiaMes.status || 'apurada') : 'pendente'} variant={guiaMes ? 'success' : 'warning'} sub={competenciaLabel(comp)} />
        <KPI label="Pendências" value={d.pendReinf + d.pendFin} variant={(d.pendReinf + d.pendFin) > 0 ? 'warning' : 'success'} sub={`${d.pendFin} contábil · ${d.pendReinf} Reinf`} />
        {k.variacao_yoy_pct != null && (
          <KPI label="vs 12m anterior" value={`${k.variacao_yoy_pct >= 0 ? '+' : ''}${k.variacao_yoy_pct.toFixed(1)}%`} variant={k.variacao_yoy_pct >= 0 ? 'success' : 'danger'} sub={fmtMoeda(k.total_12m_anterior)} />
        )}
      </div>

      {/* FAIXA 3 — O que fazer agora */}
      <div className="card mb-2" style={{ borderLeft: '3px solid #C4956A' }}>
        <div className="card__title">O que fazer agora</div>
        {acoes.length === 0 ? (
          <div style={{ padding: '12px 4px', color: 'var(--text-2)', fontSize: 14 }}>✓ Tudo em dia neste cliente para {competenciaLabel(comp)}.</div>
        ) : acoes.map((a, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 4px', borderTop: i ? '1px solid var(--border)' : 'none' }}>
            <span style={{ fontSize: 14 }}>
              <span style={{ color: a.urg ? '#fb923c' : '#64748b', marginRight: 8 }}>{a.urg ? '⚠' : '◐'}</span>{a.txt}
            </span>
            <button onClick={() => onNavigate(a.go)} style={btnGo}>{a.btn} →</button>
          </div>
        ))}
      </div>

      {/* FAIXA 4 — Saúde dos módulos */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
        {modulos.map((mod) => (
          <div key={mod.nome} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: 99, background: dot[mod.tom] }} />
              <span style={{ fontSize: 22 }}>{mod.icon}</span>
              <span style={{ fontWeight: 700, fontSize: 16 }}>{mod.nome}</span>
            </div>
            <div style={{ fontSize: 14, color: 'var(--text-1)' }}>{mod.m1}</div>
            <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{mod.m2}</div>
            <button onClick={() => onNavigate(mod.go)} style={{ ...btnGo, marginTop: 'auto', alignSelf: 'flex-start' }}>{mod.btn} →</button>
          </div>
        ))}
      </div>
    </>
  )
}

const btnGo = {
  padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)',
  background: 'var(--surface-2, rgba(196,149,106,0.12))', color: 'var(--text-1)',
  cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
}
